import { DayButton } from 'react-day-picker';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils/cn';
import styles from './StanCalendarDayButton.module.css';

function StanCalendarDayButton({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) {
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (modifiers.focused) buttonRef.current?.focus();
    }, [modifiers.focused]);

    // Format date for screen readers
    const formattedDate = day.date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    // Determine selection state for aria-label
    const getAriaLabel = () => {
        let label = formattedDate;

        if (modifiers.selected) {
            if (modifiers.range_start && modifiers.range_end) {
                label += ', selected as start and end of range';
            } else if (modifiers.range_start) {
                label += ', selected as start of range';
            } else if (modifiers.range_end) {
                label += ', selected as end of range';
            } else if (modifiers.range_middle) {
                label += ', in selected range';
            } else {
                label += ', selected';
            }
        }

        if (modifiers.today) {
            label += ', today';
        }

        if (modifiers.disabled) {
            label += ', unavailable';
        }

        return label;
    };

    return (
        <button
            ref={buttonRef}
            aria-label={getAriaLabel()}
            aria-selected={modifiers.selected}
            aria-current={modifiers.today ? 'date' : undefined}
            data-day={day.date.toLocaleDateString()}
            data-selected-single={
                modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
            }
            data-range-start={modifiers.range_start}
            data-range-end={modifiers.range_end}
            data-range-middle={modifiers.range_middle}
            className={cn(
                styles.calendarButton,
                modifiers.selected && styles.selected,
                modifiers.range_start && styles.rangeStart,
                modifiers.range_end && styles.rangeEnd,
                modifiers.range_middle && styles.rangeMiddle,
                className
            )}
            disabled={modifiers.disabled}
            {...props}
            type='button'
        >
            {day.date.getDate()}
        </button>
    );
}

export { StanCalendarDayButton };
